"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Evaluator = void 0;
exports.isInShardRange = isInShardRange;
exports.hashKey = hashKey;
exports.noneResult = noneResult;
exports.matchesRules = matchesRules;
exports.overrideResult = overrideResult;
const eppo_client_1 = require("./client/eppo-client");
const flag_evaluation_details_builder_1 = require("./flag-evaluation-details-builder");
const flag_evaluation_error_1 = require("./flag-evaluation-error");
const rules_1 = require("./rules");
const sharders_1 = require("./sharders");
class Evaluator {
    constructor(sharder) {
        this.getMatchedEvaluationCodeAndDescription = (variation, allocation, split, subjectKey, expectedVariationType) => {
            if (!(0, eppo_client_1.checkValueTypeMatch)(expectedVariationType, variation.value)) {
                const { key: vKey, value: vValue } = variation;
                return {
                    flagEvaluationCode: 'ASSIGNMENT_ERROR',
                    flagEvaluationDescription: `Variation (${vKey}) is configured for type ${expectedVariationType}, but is set to incompatible value (${vValue})`,
                };
            }
            const hasDefinedRules = !!allocation.rules?.length;
            const isExperiment = allocation.splits.length > 1;
            const isPartialRollout = split.shards.length > 1;
            const isExperimentOrPartialRollout = isExperiment || isPartialRollout;
            if (hasDefinedRules && isExperimentOrPartialRollout) {
                return {
                    flagEvaluationCode: 'MATCH',
                    flagEvaluationDescription: `Supplied attributes match rules defined in allocation "${allocation.key}" and ${subjectKey} belongs to the range of traffic assigned to "${split.variationKey}".`,
                };
            }
            if (hasDefinedRules && !isExperimentOrPartialRollout) {
                return {
                    flagEvaluationCode: 'MATCH',
                    flagEvaluationDescription: `Supplied attributes match rules defined in allocation "${allocation.key}".`,
                };
            }
            return {
                flagEvaluationCode: 'MATCH',
                flagEvaluationDescription: `${subjectKey} belongs to the range of traffic assigned to "${split.variationKey}" defined in allocation "${allocation.key}".`,
            };
        };
        this.sharder = sharder ?? new sharders_1.MD5Sharder();
    }
    evaluateFlag(flag, configDetails, subjectKey, subjectAttributes, obfuscated, expectedVariationType) {
        const flagEvaluationDetailsBuilder = new flag_evaluation_details_builder_1.FlagEvaluationDetailsBuilder(configDetails.configEnvironment.name, flag.allocations, configDetails.configFetchedAt, configDetails.configPublishedAt);
        try {
            if (!flag.enabled) {
                return noneResult(flag.key, subjectKey, subjectAttributes, flagEvaluationDetailsBuilder.buildForNoneResult('FLAG_UNRECOGNIZED_OR_DISABLED', `Unrecognized or disabled flag: ${flag.key}`), configDetails.configFormat);
            }
            const now = new Date();
            for (let i = 0; i < flag.allocations.length; i++) {
                const allocation = flag.allocations[i];
                const addUnmatchedAllocation = (code) => {
                    flagEvaluationDetailsBuilder.addUnmatchedAllocation({
                        key: allocation.key,
                        allocationEvaluationCode: code,
                        orderPosition: i + 1,
                    });
                };
                if (allocation.startAt && now < new Date(allocation.startAt)) {
                    addUnmatchedAllocation(flag_evaluation_details_builder_1.AllocationEvaluationCode.BEFORE_START_TIME);
                    continue;
                }
                if (allocation.endAt && now > new Date(allocation.endAt)) {
                    addUnmatchedAllocation(flag_evaluation_details_builder_1.AllocationEvaluationCode.AFTER_END_TIME);
                    continue;
                }
                const { matched, matchedRule } = matchesRules(allocation?.rules ?? [], { id: subjectKey, ...subjectAttributes }, obfuscated);
                if (matched) {
                    for (const split of allocation.splits) {
                        if (split.shards.every((shard) => this.matchesShard(shard, subjectKey, flag.totalShards))) {
                            const variation = flag.variations[split.variationKey];
                            const { flagEvaluationCode, flagEvaluationDescription } = this.getMatchedEvaluationCodeAndDescription(variation, allocation, split, subjectKey, expectedVariationType);
                            const flagEvaluationDetails = flagEvaluationDetailsBuilder
                                .setMatch(i, variation, allocation, matchedRule, expectedVariationType)
                                .build(flagEvaluationCode, flagEvaluationDescription);
                            return {
                                flagKey: flag.key,
                                format: configDetails.configFormat,
                                subjectKey,
                                subjectAttributes,
                                allocationKey: allocation.key,
                                variation,
                                extraLogging: split.extraLogging ?? {},
                                doLog: allocation.doLog,
                                flagEvaluationDetails,
                            };
                        }
                    }
                    // matched, but does not fall within split range
                    addUnmatchedAllocation(flag_evaluation_details_builder_1.AllocationEvaluationCode.TRAFFIC_EXPOSURE_MISS);
                }
                else {
                    addUnmatchedAllocation(flag_evaluation_details_builder_1.AllocationEvaluationCode.FAILING_RULE);
                }
            }
            return noneResult(flag.key, subjectKey, subjectAttributes, flagEvaluationDetailsBuilder.buildForNoneResult('DEFAULT_ALLOCATION_NULL', 'No allocations matched. Falling back to "Default Allocation", serving NULL'), configDetails.configFormat);
        }
        catch (err) {
            console.error('>>>>', err);
            const flagEvaluationDetails = flagEvaluationDetailsBuilder.gracefulBuild('ASSIGNMENT_ERROR', `Assignment Error: ${err.message}`);
            if (flagEvaluationDetails) {
                const flagEvaluationError = new flag_evaluation_error_1.FlagEvaluationError(err.message);
                flagEvaluationError.flagEvaluationDetails = flagEvaluationDetails;
                throw flagEvaluationError;
            }
            throw err;
        }
    }
    matchesShard(shard, subjectKey, totalShards) {
        const assignedShard = this.sharder.getShard(hashKey(shard.salt, subjectKey), totalShards);
        return shard.ranges.some((range) => isInShardRange(assignedShard, range));
    }
}
exports.Evaluator = Evaluator;
function isInShardRange(shard, range) {
    return range.start <= shard && shard < range.end;
}
function hashKey(salt, subjectKey) {
    return `${salt}-${subjectKey}`;
}
function noneResult(flagKey, subjectKey, subjectAttributes, flagEvaluationDetails, format) {
    return {
        flagKey,
        format,
        subjectKey,
        subjectAttributes,
        allocationKey: null,
        variation: null,
        extraLogging: {},
        doLog: false,
        flagEvaluationDetails,
    };
}
function matchesRules(rules, subjectAttributes, obfuscated) {
    if (!rules.length) {
        return {
            matched: true,
            matchedRule: null,
        };
    }
    let matchedRule = null;
    const hasMatch = rules.some((rule) => {
        const matched = (0, rules_1.matchesRule)(rule, subjectAttributes, obfuscated);
        if (matched) {
            matchedRule = rule;
        }
        return matched;
    });
    return hasMatch
        ? {
            matched: true,
            matchedRule,
        }
        : {
            matched: false,
            matchedRule: null,
        };
}
function overrideResult(flagKey, subjectKey, subjectAttributes, overrideVariation, flagEvaluationDetailsBuilder) {
    const overrideAllocationKey = 'override-' + overrideVariation.key;
    const flagEvaluationDetails = flagEvaluationDetailsBuilder
        .setMatch(0, overrideVariation, { key: overrideAllocationKey, splits: [], doLog: false }, null, undefined)
        .build('MATCH', 'Flag override applied');
    return {
        flagKey,
        subjectKey,
        variation: overrideVariation,
        subjectAttributes,
        flagEvaluationDetails,
        doLog: false,
        format: '',
        allocationKey: overrideAllocationKey,
        extraLogging: {},
    };
}
//# sourceMappingURL=evaluator.js.map